import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { StoreService } from '../store.service';
import { combineLatest, distinctUntilChanged, Observable } from 'rxjs';
import { Store } from '../store';
import { map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import { Product } from '../../product/product';
import { ProductService } from '../../product/product.service';
import { Meta, MetaDefinition, Title } from '@angular/platform-browser';
import { CanonicalService } from '../../core/canonical.service';
import { environment } from '../../../environments/environment';
import { PagedProducts } from '../../product/paged-products';

@Component({
  selector: 'app-public-store',
  templateUrl: './public-store.component.html',
  styleUrls: ['./public-store.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicStoreComponent implements OnInit {
  store$: Observable<Store> | undefined;
  storeProducts$: Observable<Product[]> | undefined;
  page = 1;
  lastPage = 1;
  totalProducts = 0;
  productsPerPage = 16;
  storeId = '';

  constructor(
    private readonly route: ActivatedRoute,
    private readonly storeService: StoreService,
    readonly productService: ProductService,
    private router: Router,
    private meta: Meta,
    private title: Title,
    private readonly canonicalService: CanonicalService
  ) {}

  ngOnInit(): void {
    const page$ = this.route.queryParamMap.pipe(
      map((params) => this.getPageNumberFromParamMap(params)),
      distinctUntilChanged()
    );
    this.store$ = this.route.params.pipe(
      mergeMap((params) => this.storeService.getOneByIdPublic(params['id'])),
      tap((store) => (this.storeId = store?.id!)),
      tap((store) => this.setMeta(store)),
      shareReplay(1)
    );
    this.storeProducts$ = combineLatest([this.store$, page$]).pipe(
      mergeMap(([store, page]) =>
        this.productService.getAllPagedStore(
          store?.id!,
          page,
          this.productsPerPage
        )
      ),
      tap((res) => this.setPageFromResults(res)),
      map((res) => res.products)
    );
  }

  getProductsForPage(page: number): void {
    this.page = page;
  }

  private getPageNumberFromParamMap(params: ParamMap): number {
    return params.has('page') ? +params.get('page')! : 1;
  }

  private setPageFromResults(res: PagedProducts) {
    this.totalProducts = res.count;
    this.lastPage = Math.ceil(this.totalProducts / this.productsPerPage);
    const page = this.route.snapshot.queryParamMap.get('page');
    if (page) {
      if (+page > this.lastPage && this.lastPage !== 0) {
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { page: this.lastPage },
          queryParamsHandling: 'merge',
        });
      } else {
        this.page = +page;
      }
    }
  }

  getStoreImage(store: Store): string {
    return this.storeService.getImageUrlCached(store);
  }

  setMeta(store: Store): void {
    const imageURL = this.getStoreImage(store);
    const metaTags: MetaDefinition[] = [
      { name: 'author', content: store.name },
      {
        name: 'description',
        content: store.description || `Boutique ${store.name}`,
      },
      {
        property: 'og:url',
        content: environment.baseURL + this.router.routerState.snapshot.url,
      },
      { property: 'og:title', content: store.name },
      {
        property: 'og:description',
        content: store.description || `Boutique ${store.name}`,
      },
      { property: 'og:image', content: imageURL },
    ];
    metaTags.forEach((m) => this.meta.updateTag(m));
    this.title.setTitle(store.name);
    this.canonicalService.setCanonicalURL();
  }
}
